import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { toast } from 'sonner';
import {
  Inbox, Mail, MailOpen, Search, Trash2, FileText, Receipt, User,
  RefreshCw, ExternalLink, Loader2, CheckCheck, Reply, Send, ChevronDown, ChevronLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    return d.toLocaleDateString();
  } catch {
    return iso;
  }
}

export default function ClientReplies() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(''); // search
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Reply composer state
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);


  const loadMessages = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentWorkspace?.workspace_id) params.set('workspace_id', currentWorkspace.workspace_id);
      if (showUnreadOnly) params.set('unread_only', 'true');
      params.set('limit', '100');
      const r = await apiFetch(`${API_URL}/api/inbound/messages?${params.toString()}`);
      const data = await r.json();
      setMessages(data.messages || []);
      setUnreadCount(data.unread_count || 0);
      if (!activeId && data.messages?.length) {
        setActiveId(data.messages[0].inbound_id);
      }
    } catch (e) {
      toast.error('Could not load replies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMessages();
    // eslint-disable-next-line
  }, [user, currentWorkspace?.workspace_id, showUnreadOnly]);

  const active = useMemo(
    () => messages.find((m) => m.inbound_id === activeId) || null,
    [messages, activeId],
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) =>
      [m.from_email, m.from_name, m.subject, m.text]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [messages, filter]);

  const markRead = async (id) => {
    try {
      await apiFetch(`${API_URL}/api/inbound/messages/${id}/read`, { method: 'POST' });
      setMessages((arr) =>
        arr.map((m) => (m.inbound_id === id ? { ...m, read: true } : m)),
      );
      setUnreadCount((n) => Math.max(0, n - 1));
    } catch {
      /* */
    }
  };

  const handleSelect = (m) => {
    setActiveId(m.inbound_id);
    setReplyOpen(false);
    setReplyText('');
    if (!m.read) markRead(m.inbound_id);
  };

  const sendReply = async () => {
    if (!active || !replyText.trim()) return;
    setSendingReply(true);
    try {
      const r = await apiFetch(`${API_URL}/api/inbound/messages/${active.inbound_id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: replyText }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Send failed');
      }
      toast.success(`Reply sent to ${active.from_email}`);
      setReplyText('');
      setReplyOpen(false);
      // Reload to surface the new replies_sent entry
      await loadMessages();
    } catch (e) {
      toast.error(typeof e?.message === 'string' ? e.message : 'Send failed');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this reply? This cannot be undone.')) return;
    try {
      await apiFetch(`${API_URL}/api/inbound/messages/${id}`, { method: 'DELETE' });
      setMessages((arr) => arr.filter((m) => m.inbound_id !== id));
      if (activeId === id) setActiveId(null);
      toast.success('Reply deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const markAllRead = async () => {
    const unread = messages.filter((m) => !m.read);
    if (!unread.length) return;
    await Promise.all(
      unread.map((m) =>
        apiFetch(`${API_URL}/api/inbound/messages/${m.inbound_id}/read`, { method: 'POST' }),
      ),
    );
    setMessages((arr) => arr.map((m) => ({ ...m, read: true })));
    setUnreadCount(0);
    toast.success('All replies marked read');
  };

  return (
    <div
      className="flex h-[calc(100vh-3.5rem-4rem)] lg:h-[calc(100vh-3.5rem)] bg-zinc-50 dark:bg-black text-zinc-900 dark:text-zinc-100"
      data-testid="client-replies-page"
    >
      {/* ── List sidebar (full-width on mobile when no message selected, 380px on md+) ── */}
      <aside
        className={cn(
          'flex-shrink-0 flex flex-col border-r border-zinc-200 dark:border-white/5 bg-white/60 dark:bg-zinc-950/40 backdrop-blur-xl',
          'w-full md:w-[380px]',
          // On mobile, hide the list when a message is open so the detail pane gets the full screen.
          active ? 'hidden md:flex' : 'flex',
        )}
      >
        <div className="px-4 py-4 border-b border-zinc-200 dark:border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Inbox className="w-5 h-5 text-emerald-500" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 dark:text-zinc-600 -mb-0.5">
                Inbox
              </p>
              <h1 className="text-sm font-semibold truncate">Client Replies</h1>
            </div>
            {unreadCount > 0 && (
              <Badge
                variant="secondary"
                className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                data-testid="client-replies-unread-badge"
              >
                {unreadCount}
              </Badge>
            )}
          </div>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search replies…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-9 pl-8 text-sm bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-white/10"
              data-testid="client-replies-search"
            />
          </div>
          <div className="flex items-center gap-1 mt-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowUnreadOnly((v) => !v)}
              className={cn(
                'h-7 text-xs',
                showUnreadOnly && 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300',
              )}
              data-testid="client-replies-unread-toggle"
            >
              {showUnreadOnly ? 'Showing unread' : 'All replies'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={loadMessages}
              disabled={loading}
              className="h-7 w-7 p-0 ml-auto"
              aria-label="Refresh"
              data-testid="client-replies-refresh"
            >
              <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
            </Button>
            {unreadCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={markAllRead}
                className="h-7 w-7 p-0"
                aria-label="Mark all read"
                title="Mark all read"
                data-testid="client-replies-mark-all-read"
              >
                <CheckCheck className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div data-testid="client-replies-list">
            {loading && messages.length === 0 && (
              <div className="px-4 py-8 text-center text-zinc-500 text-sm">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-4 py-12 text-center text-zinc-500 text-sm" data-testid="client-replies-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium text-zinc-700 dark:text-zinc-300 mb-1">No replies yet</p>
                <p className="text-xs">
                  When clients reply to invoice or bill emails, their messages will appear here.
                </p>
              </div>
            )}
            {filtered.map((m) => (
              <button
                key={m.inbound_id}
                onClick={() => handleSelect(m)}
                className={cn(
                  'w-full text-left px-4 py-3 border-b border-zinc-200/60 dark:border-white/5 transition-colors',
                  'hover:bg-zinc-100 dark:hover:bg-white/5',
                  activeId === m.inbound_id && 'bg-emerald-500/5 dark:bg-emerald-500/10 border-l-2 border-l-emerald-500',
                  !m.read && 'bg-white dark:bg-zinc-950/60',
                )}
                data-testid={`client-replies-item-${m.inbound_id}`}
              >
                <div className="flex items-start gap-2">
                  {m.read ? (
                    <MailOpen className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Mail className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className={cn(
                        'text-sm truncate',
                        m.read ? 'font-normal text-zinc-700 dark:text-zinc-300' : 'font-semibold text-zinc-900 dark:text-zinc-100',
                      )}>
                        {m.from_name || m.from_email || 'Unknown sender'}
                      </span>
                      <span className="ml-auto text-[10px] text-zinc-500 flex-shrink-0">
                        {formatWhen(m.received_at)}
                      </span>
                    </div>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 truncate mt-0.5">
                      {m.subject || '(no subject)'}
                    </p>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-500 truncate mt-0.5">
                      {(m.text || '').slice(0, 80)}
                    </p>
                    {(m.invoice_id || m.bill_id || m.contact_id) && (
                      <div className="flex items-center gap-1 mt-1.5">
                        {m.invoice_id && (
                          <Badge variant="outline" className="h-4 text-[9px] font-mono uppercase tracking-wider px-1">
                            <FileText className="w-2.5 h-2.5 mr-0.5" /> Invoice
                          </Badge>
                        )}
                        {m.bill_id && (
                          <Badge variant="outline" className="h-4 text-[9px] font-mono uppercase tracking-wider px-1">
                            <Receipt className="w-2.5 h-2.5 mr-0.5" /> Bill
                          </Badge>
                        )}
                        {!m.invoice_id && !m.bill_id && m.contact_id && (
                          <Badge variant="outline" className="h-4 text-[9px] font-mono uppercase tracking-wider px-1">
                            <User className="w-2.5 h-2.5 mr-0.5" /> Contact
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </ScrollArea>
      </aside>

      {/* ── Detail pane ─────────────────────────────────────────── */}
      <main className={cn(
        'flex-1 flex flex-col',
        // On mobile, only show detail pane when a message is selected.
        active ? 'flex' : 'hidden md:flex',
      )}>
        {!active && (
          <div className="flex-1 flex items-center justify-center text-zinc-500" data-testid="client-replies-empty-detail">
            <div className="text-center">
              <Inbox className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select a reply to view it.</p>
            </div>
          </div>
        )}
        {active && (
          <>
            <div className="border-b border-zinc-200 dark:border-white/5 px-4 sm:px-6 py-4 bg-white/60 dark:bg-black/30 backdrop-blur-xl">
              <div className="flex items-start gap-3">
                {/* Mobile-only back button — returns to the list */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setActiveId(null)}
                  className="md:hidden -ml-2 h-8 w-8 p-0"
                  aria-label="Back"
                  data-testid="client-replies-mobile-back"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg font-semibold truncate" data-testid="client-replies-detail-subject">
                    {active.subject || '(no subject)'}
                  </h2>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1">
                    From{' '}
                    <span className="font-medium">
                      {active.from_name ? `${active.from_name} <${active.from_email}>` : active.from_email}
                    </span>
                    <span className="mx-1.5 text-zinc-400">·</span>
                    {formatWhen(active.received_at)}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end shrink-0">
                  <Button
                    size="sm"
                    onClick={() => { setReplyOpen(true); setReplyText(''); }}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="client-replies-reply-btn"
                  >
                    <Reply className="w-3.5 h-3.5 sm:mr-1.5" />
                    <span className="hidden sm:inline">Reply</span>
                  </Button>
                  {active.invoice_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/invoices?preview=${active.invoice_id}`)}
                      data-testid="client-replies-open-invoice"
                    >
                      <FileText className="w-3.5 h-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Open invoice</span>
                      <ExternalLink className="w-3 h-3 ml-1 hidden sm:inline-block" />
                    </Button>
                  )}
                  {active.bill_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/bills/${active.bill_id}`)}
                      data-testid="client-replies-open-bill"
                    >
                      <Receipt className="w-3.5 h-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Open bill</span>
                      <ExternalLink className="w-3 h-3 ml-1 hidden sm:inline-block" />
                    </Button>
                  )}
                  {active.contact_id && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/contacts/${active.contact_id}`)}
                      data-testid="client-replies-open-contact"
                    >
                      <User className="w-3.5 h-3.5 sm:mr-1.5" />
                      <span className="hidden sm:inline">Contact</span>
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(active.inbound_id)}
                    className="text-red-500 hover:bg-red-500/10"
                    aria-label="Delete"
                    data-testid="client-replies-delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
            <ScrollArea className="flex-1">
              <div className="max-w-3xl mx-auto px-6 py-6" data-testid="client-replies-detail-body">
                {active.html ? (
                  <div
                    className="prose prose-sm dark:prose-invert max-w-none"
                    // Customer-supplied HTML — sanitised with DOMPurify to strip
                    // <script>, on* handlers, and javascript: URIs.
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(active.html, {
                        USE_PROFILES: { html: true },
                        FORBID_TAGS: ['style', 'iframe', 'form'],
                        FORBID_ATTR: ['onerror', 'onload', 'onclick'],
                      }),
                    }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-zinc-800 dark:text-zinc-200 font-sans">
                    {active.text || '(empty body)'}
                  </pre>
                )}

                {active.attachments?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-zinc-200 dark:border-white/10">
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-2">
                      Attachments ({active.attachments.length})
                    </p>
                    <div className="space-y-1.5">
                      {active.attachments.map((a, i) => (
                        <div
                          key={a.id || i}
                          className="flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-100 dark:bg-zinc-900 border border-zinc-200 dark:border-white/5 text-xs"
                        >
                          <FileText className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="font-medium">{a.filename || 'attachment'}</span>
                          <span className="text-zinc-500 ml-auto">{a.content_type}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sent replies history — shows when you've responded to this thread */}
                {active.replies_sent?.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-zinc-200 dark:border-white/10" data-testid="client-replies-sent-history">
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-zinc-500 mb-3">
                      Your replies ({active.replies_sent.length})
                    </p>
                    <div className="space-y-3">
                      {active.replies_sent.map((rep, i) => (
                        <div key={rep.email_id || i} className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                          <div className="flex items-center gap-2 text-[11px] text-zinc-600 dark:text-zinc-400 mb-1.5">
                            <Send className="w-3 h-3 text-emerald-500" />
                            <span className="font-medium">{rep.sent_by_email}</span>
                            <span className="text-zinc-400">·</span>
                            <span>{formatWhen(rep.sent_at)}</span>
                            <span className="text-zinc-400">·</span>
                            <span className="truncate">to {rep.to_email}</span>
                          </div>
                          <p className="text-sm text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">
                            {rep.body_preview}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* ── Composer (collapsible) ─────────────────────────── */}
            {replyOpen && (
              <div
                className="border-t border-zinc-200 dark:border-white/5 bg-white/80 dark:bg-zinc-950/70 backdrop-blur-xl px-6 py-4"
                data-testid="client-replies-composer"
              >
                <div className="max-w-3xl mx-auto">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-zinc-600 dark:text-zinc-400">
                      Replying to <span className="font-medium">{active.from_email}</span>
                      <span className="ml-2 text-zinc-400">·</span>
                      <span className="ml-2 font-mono">Re: {(active.subject || '').replace(/^re:\s*/i, '') || '(no subject)'}</span>
                    </p>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setReplyOpen(false); setReplyText(''); }}
                      className="h-6 px-2 text-xs"
                      data-testid="client-replies-composer-cancel"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <Textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply…"
                    rows={5}
                    className="resize-none text-sm border-zinc-200 dark:border-white/10 bg-zinc-50 dark:bg-zinc-900"
                    disabled={sendingReply}
                    data-testid="client-replies-composer-textarea"
                    autoFocus
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-[11px] text-zinc-500">
                      Threads in-conversation. Customer replies route back to this inbox.
                    </p>
                    <Button
                      size="sm"
                      onClick={sendReply}
                      disabled={!replyText.trim() || sendingReply}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white"
                      data-testid="client-replies-composer-send"
                    >
                      {sendingReply ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <Send className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      {sendingReply ? 'Sending…' : 'Send reply'}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
